import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { toast } from "sonner";
import {
  User,
  Calendar,
  Globe,
  Save,
  ExternalLink,
  Camera,
  Phone,
  Mail,
  Linkedin,
  Code,
  Loader2,
} from "lucide-react";

const AdminPreferences = () => {
  const { token, API, user, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Profile data
  const [profile, setProfile] = useState({
    bio: "",
    phone: "",
    linkedin: "",
    display_email: "",
    profile_photo: null,
    booking_link: "",
  });

  // Tracking settings
  const [tracking, setTracking] = useState({
    google_analytics_id: "",
    google_tag_manager_id: "",
    meta_pixel_id: "",
    custom_head_scripts: "",
    custom_body_scripts: "",
  });

  useEffect(() => {
    if (token && API) {
      loadData();
    }
  }, [token, API]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch profile
      const profileRes = await fetch(`${API}/admin/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile({
          bio: data.bio || "",
          phone: data.phone || "",
          linkedin: data.linkedin || "",
          display_email: data.display_email || user?.email || "",
          profile_photo: data.profile_photo || null,
          booking_link: data.booking_link || "",
        });
      }

      // Fetch site settings
      const settingsRes = await fetch(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setTracking({
          google_analytics_id: data.google_analytics_id || "",
          google_tag_manager_id: data.google_tag_manager_id || "",
          meta_pixel_id: data.meta_pixel_id || "",
          custom_head_scripts: data.custom_head_scripts || "",
          custom_body_scripts: data.custom_body_scripts || "",
        });
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        bio: profile.bio,
        phone: profile.phone,
        linkedin: profile.linkedin,
        display_email: profile.display_email,
        booking_link: profile.booking_link,
      });

      const response = await fetch(`${API}/admin/profile?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Profile saved successfully");
      } else {
        toast.error("Failed to save profile");
      }
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const saveTracking = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(tracking).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value);
        }
      });

      const response = await fetch(`${API}/admin/site-settings?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/admin/profile/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, profile_photo: data.photo_url }));
        toast.success("Photo uploaded successfully");
      } else {
        toast.error("Failed to upload photo");
      }
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeNav="preferences">
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#2471A3]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeNav="preferences">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#1A2636] mb-2">Preferences</h1>
          <p className="text-gray-600">Manage your profile, calendar link, and tracking settings.</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 border-b">
          <button
            onClick={() => setActiveTab("profile")}
            className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "profile"
                ? "text-[#2471A3] border-[#2471A3]"
                : "text-gray-500 border-transparent hover:text-gray-700"
            }`}
          >
            <User className="h-4 w-4" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab("calendar")}
            className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "calendar"
                ? "text-[#2471A3] border-[#2471A3]"
                : "text-gray-500 border-transparent hover:text-gray-700"
            }`}
          >
            <Calendar className="h-4 w-4" />
            Calendar
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setActiveTab("tracking")}
              className={`px-4 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === "tracking"
                  ? "text-[#2471A3] border-[#2471A3]"
                  : "text-gray-500 border-transparent hover:text-gray-700"
              }`}
            >
              <Globe className="h-4 w-4" />
              Tracking
            </button>
          )}
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-[#2471A3]" />
                  Your Profile
                </CardTitle>
                <CardDescription>
                  This information will be visible to your assigned clients.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Photo Upload */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <Avatar className="h-24 w-24">
                      {profile.profile_photo ? (
                        <AvatarImage src={profile.profile_photo} alt="Profile" />
                      ) : (
                        <AvatarFallback className="bg-[#2471A3] text-white text-2xl">
                          {user?.name?.charAt(0) || "A"}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg border hover:bg-gray-50"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Camera className="h-4 w-4 text-gray-600" />
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A2636]">{user?.name || "Admin"}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <p className="text-xs text-gray-400 mt-1">Max 5MB, JPG, PNG, or WebP</p>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      Display Email
                    </Label>
                    <Input
                      value={profile.display_email}
                      onChange={(e) => setProfile(p => ({ ...p, display_email: e.target.value }))}
                      placeholder="Email shown to clients"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Can be different from your login email</p>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      Phone Number
                    </Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Linkedin className="h-4 w-4 text-gray-400" />
                      LinkedIn URL
                    </Label>
                    <Input
                      value={profile.linkedin}
                      onChange={(e) => setProfile(p => ({ ...p, linkedin: e.target.value }))}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={profile.bio}
                      onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                      placeholder="Tell clients about yourself..."
                      rows={4}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">{profile.bio.length}/500 characters</p>
                  </div>
                </div>

                <Button onClick={saveProfile} disabled={saving} className="bg-[#2471A3] hover:bg-[#1A5276]">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Profile
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#2471A3]" />
                  Calendar Booking Link
                </CardTitle>
                <CardDescription>
                  Enter your calendar scheduling link (Calendly, Cal.com, etc.). Clients will use this to book appointments.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Booking Link</Label>
                  <Input
                    value={profile.booking_link}
                    onChange={(e) => setProfile(p => ({ ...p, booking_link: e.target.value }))}
                    placeholder="https://calendly.com/your-name/meeting"
                    className="mt-1"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    This link appears when clients click "Book Appointment" in their portal.
                  </p>
                </div>

                <Button onClick={saveProfile} disabled={saving} className="bg-[#2471A3] hover:bg-[#1A5276]">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Booking Link
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gray-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <ExternalLink className="h-5 w-5 text-[#2471A3] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-[#1A2636] mb-1">Recommended Services</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" className="text-[#2471A3] hover:underline">Calendly</a> - Popular scheduling tool</li>
                      <li>• <a href="https://cal.com" target="_blank" rel="noopener noreferrer" className="text-[#2471A3] hover:underline">Cal.com</a> - Open source alternative</li>
                      <li>• <a href="https://acuityscheduling.com" target="_blank" rel="noopener noreferrer" className="text-[#2471A3] hover:underline">Acuity</a> - Feature-rich scheduling</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tracking Tab - Super Admin Only */}
        {activeTab === "tracking" && isSuperAdmin && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-[#2471A3]" />
                  Analytics & Tracking
                </CardTitle>
                <CardDescription>
                  Add tracking codes to monitor website performance. These apply to public pages only.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Google Analytics 4 ID</Label>
                  <Input
                    value={tracking.google_analytics_id}
                    onChange={(e) => setTracking(t => ({ ...t, google_analytics_id: e.target.value }))}
                    placeholder="G-XXXXXXXXXX"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Google Tag Manager ID</Label>
                  <Input
                    value={tracking.google_tag_manager_id}
                    onChange={(e) => setTracking(t => ({ ...t, google_tag_manager_id: e.target.value }))}
                    placeholder="GTM-XXXXXXX"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Meta (Facebook) Pixel ID</Label>
                  <Input
                    value={tracking.meta_pixel_id}
                    onChange={(e) => setTracking(t => ({ ...t, meta_pixel_id: e.target.value }))}
                    placeholder="XXXXXXXXXXXXXXXX"
                    className="mt-1"
                  />
                </div>

                <Button onClick={saveTracking} disabled={saving} className="bg-[#2471A3] hover:bg-[#1A5276]">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Tracking Settings
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5 text-[#2471A3]" />
                  Custom Scripts
                </CardTitle>
                <CardDescription>
                  Add custom JavaScript to the page head or body. Use with caution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Head Scripts</Label>
                  <Textarea
                    value={tracking.custom_head_scripts}
                    onChange={(e) => setTracking(t => ({ ...t, custom_head_scripts: e.target.value }))}
                    placeholder="<!-- Scripts added to <head> -->"
                    rows={4}
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <div>
                  <Label>Body Scripts</Label>
                  <Textarea
                    value={tracking.custom_body_scripts}
                    onChange={(e) => setTracking(t => ({ ...t, custom_body_scripts: e.target.value }))}
                    placeholder="<!-- Scripts added before </body> -->"
                    rows={4}
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <Button onClick={saveTracking} disabled={saving} className="bg-[#2471A3] hover:bg-[#1A5276]">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Custom Scripts
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPreferences;
